import { BaseCommand } from './BaseCommand.js';
import { ConfigLoader } from '../config/ConfigLoader.js';
import * as path from 'path';
import fs from 'fs-extra';
import { promises as fsPromises } from 'fs';

export interface TestOptions {
	watch?: boolean;
	coverage?: boolean;
	ui?: boolean;
	run?: boolean;
	reporter?: string;
	filter?: string;
}

export class TestCommand extends BaseCommand {
	async execute(options: TestOptions): Promise<void> {
		try {
			const configLoader = new ConfigLoader(this.cwd);
			const frameConfig = await configLoader.load();

			console.log('[Frame Test] Initializing test environment...');

			// Check if test configuration exists
			const hasTestConfig = await this.checkTestSetup();

			if (!hasTestConfig) {
				console.log('[Frame Test] No test configuration found. Setting up tests...');
				await this.setupTesting(frameConfig.devTools?.testing || {});
			}

			// Run tests based on the configured framework
			const framework = frameConfig.devTools?.testing?.framework || 'vitest';

			if (framework === 'vitest') {
				await this.runVitest(options);
			} else if (framework === 'jest') {
				await this.runJest(options);
			}

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			console.error('[Frame Test] Test execution failed:', errorMessage);
			process.exit(1);
		}
	}

	private async checkTestSetup(): Promise<boolean> {
		const vitestConfig = path.join(this.cwd, 'vitest.config.ts');
		const jestConfig = path.join(this.cwd, 'jest.config.js');

		return await fs.pathExists(vitestConfig) || await fs.pathExists(jestConfig);
	}

	private async setupTesting(testConfig: any): Promise<void> {
		const framework = testConfig.framework || 'vitest';

		// Create test directory structure
		const testDir = path.join(this.cwd, 'tests');
		await fs.ensureDir(testDir);
		await fs.ensureDir(path.join(testDir, 'unit'));
		await fs.ensureDir(path.join(testDir, 'integration'));
		await fs.ensureDir(path.join(testDir, 'utils'));

		// Generate test utilities
		await this.generateTestUtils(testDir);

		// Generate sample tests
		await this.generateSampleTests(testDir);

		// Update package.json with test scripts
		await this.updatePackageJsonScripts(framework);

		console.log(`[Frame Test] ${framework} testing setup completed!`);
	}

	private async generateTestUtils(testDir: string): Promise<void> {
		const testUtils = `// Frame Test Utilities
import { mount, VueWrapper } from '@vue/test-utils'
import { createRouter, createWebHistory } from 'vue-router'
import { createPinia } from 'pinia'

// Create test router
export function createTestRouter(routes: any[] = []) {
	return createRouter({
		history: createWebHistory(),
		routes: [
			{ path: '/', component: { template: '<div>Home</div>' } },
			...routes
		]
	})
}

// Create test store
export function createTestStore() {
	return createPinia()
}

// Mount component with Frame dependencies
export function mountWithFrame(component: any, options: any = {}) {
	const router = createTestRouter(options.routes)
	const pinia = createTestStore()
	
	return mount(component, {
		global: {
			plugins: [router, pinia],
			...options.global
		},
		...options
	})
}

// Test data factories
export const testData = {
	user: () => ({
		id: 1,
		name: 'Test User',
		email: 'test@example.com'
	}),
	
	route: (overrides = {}) => ({
		path: '/test',
		name: 'Test',
		params: {},
		query: {},
		...overrides
	})
}

// Mock Frame utilities
export const mockFrame = {
	router: {
		push: vi.fn(),
		replace: vi.fn(),
		go: vi.fn()
	},
	
	assets: {
		getAssetUrl: vi.fn(() => '/mock-asset.png'),
		importImage: vi.fn(() => '/mock-image.png')
	}
}
`;

		await fs.outputFile(path.join(testDir, 'utils', 'test-utils.ts'), testUtils);
	}

	private async generateSampleTests(testDir: string): Promise<void> {
		// Generate sample component test
		const componentTest = `import { describe, it, expect } from 'vitest'
import { mountWithFrame } from '../utils/test-utils'

// Example component test
describe('Home Component', () => {
	it('renders welcome message', () => {
		const wrapper = mountWithFrame({
			template: '<div class="home"><h1>Welcome to Frame!</h1></div>'
		})
		
		expect(wrapper.find('h1').text()).toBe('Welcome to Frame!')
		expect(wrapper.classes()).toContain('home')
	})
	
	it('handles routing correctly', async () => {
		const wrapper = mountWithFrame({
			template: '<router-link to="/about">About</router-link>'
		})
		
		const link = wrapper.find('a')
		expect(link.attributes('href')).toBe('/about')
	})
})
`;

		await fs.outputFile(path.join(testDir, 'unit', 'Home.spec.ts'), componentTest);

		// Generate integration test example
		const integrationTest = `import { describe, it, expect } from 'vitest'
import { mountWithFrame, testData } from '../utils/test-utils'

describe('Frame Integration', () => {
	it('handles full application flow', async () => {
		const user = testData.user()
		
		const wrapper = mountWithFrame({
			template: \`
				<div>
					<h1>User: {{ user.name }}</h1>
					<router-view />
				</div>
			\`,
			data() {
				return { user }
			}
		})
		
		expect(wrapper.find('h1').text()).toContain('Test User')
	})
})
`;

		await fs.outputFile(path.join(testDir, 'integration', 'app.spec.ts'), integrationTest);
	}

	private async updatePackageJsonScripts(framework: string): Promise<void> {
		const packageJsonPath = path.join(this.cwd, 'package.json');

		if (await fs.pathExists(packageJsonPath)) {
			const packageJson = JSON.parse(await fsPromises.readFile(packageJsonPath, "utf8"));

			packageJson.scripts = packageJson.scripts || {};

			if (framework === 'vitest') {
				packageJson.scripts.test = 'vitest';
				packageJson.scripts['test:run'] = 'vitest run';
				packageJson.scripts['test:coverage'] = 'vitest run --coverage';
				packageJson.scripts['test:ui'] = 'vitest --ui';
			} else {
				packageJson.scripts.test = 'jest';
				packageJson.scripts['test:watch'] = 'jest --watch';
				packageJson.scripts['test:coverage'] = 'jest --coverage';
			}

			await fs.outputFile(packageJsonPath, JSON.stringify(packageJson, null, 2));
		}
	}

	private async runVitest(options: TestOptions): Promise<void> {
		const args = ['vitest'];

		if (options.run) {
			args.push('run');
		}

		if (options.coverage) {
			args.push('--coverage');
		}

		if (options.ui) {
			args.push('--ui');
		}

		if (options.reporter) {
			args.push('--reporter', options.reporter);
		}

		if (options.filter) {
			args.push(options.filter);
		}

		console.log('[Frame Test] Running Vitest...');
		console.log(`Command: npx ${args.join(' ')}`);

		// In a real implementation, we would spawn the process
		// For now, just show what would be executed
		console.log('[Frame Test] Test execution would run here');
	}

	private async runJest(options: TestOptions): Promise<void> {
		const args = ['jest'];

		if (options.watch) {
			args.push('--watch');
		}

		if (options.coverage) {
			args.push('--coverage');
		}

		if (options.filter) {
			args.push(options.filter);
		}

		console.log('[Frame Test] Running Jest...');
		console.log(`Command: npx ${args.join(' ')}`);

		// In a real implementation, we would spawn the process
		console.log('[Frame Test] Test execution would run here');
	}
} 